'use client'
import { apiVerifyCaptcha } from '@/apis/captcha';
import { apiSubscribeNewsletter } from '@/apis/page_optimization';
import { faPaperPlane, FontAwesomeIcon } from '@/constants/icons';
import React, { useState } from 'react'
import { toast } from "react-toastify";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface Props {
    companyId: string;
    domain: string;
}

const FooterNewsletter = ({ companyId, domain }: Props) => {
    const [email, setEmail] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [status, setStatus] = useState<'idle' | 'verifying' | 'sending' | 'failed'>('idle');
    const { executeRecaptcha } = useGoogleReCaptcha();

    const validateEmail = (email: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleRate = async (e: React.FormEvent, p_email: string) => {
        e.preventDefault();

        if (!validateEmail(p_email)) {
            setError("Please enter a valid email address.");
            toast.error("Please enter a valid email address.");
            return;
        }
        setError(""); // Clear previous error if valid
        await handleSubmit(p_email);
    };

    const handleSubmit = async (p_email: string) => {
        try {
            if (!executeRecaptcha) {
                toast.error('reCAPTCHA not ready. Please try again.', { autoClose: 2000 });
                return;
            }

            setStatus('verifying');
            const recaptchaToken = await executeRecaptcha('newsletter_form');
            const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken);

            if (!captchaRes?.verified) {
                setStatus('failed');
                toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 });
                return;
            }

            setStatus('sending');
            const response = await apiSubscribeNewsletter(companyId, p_email);
            if (response.message == "Subscribed successfully") {
                toast.success("Thank you for your feedback!", { autoClose: 2000 });
                setEmail("");
            } else {
                toast.error("You have already subscribed to our newsletter.")
            }
        } catch (error) {
            console.error("Error subscribing:", error);
            toast.error("There was an error subscribing. Please try again later.");
        } finally {
            setStatus('idle');
        }
    };

    return (
        <div className="w-full max-w-[420px]">
            <h3 className="relative mb-7 text-[13px] font-black uppercase tracking-[0.08em] text-white after:absolute after:-bottom-3 after:left-0 after:h-0.5 after:w-7 after:bg-[#5D27A1]">Newsletter</h3>
            <div className="space-y-4">
                <p className="text-[13px] font-medium leading-6 text-[#c7ccd6]">
                    Subscribe to get the latest deals & updates.
                </p>
                <form className="w-full space-y-3" onSubmit={(e) => { handleRate(e, email) }} autoComplete="off">
                    <input
                        type="email"
                        placeholder="Enter your email"
                        className="w-full rounded-lg border border-[#5D27A1]/30 bg-[#232323] px-4 py-3 text-[13px] text-white outline-none placeholder:text-[#7a9f8a] transition-all duration-300 focus:border-[#5D27A1] focus:bg-[#0d2a1f] focus:ring-2 focus:ring-[#5D27A1]/20"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={status !== 'idle'}
                    />
                    <button
                        type="submit"
                        disabled={status !== 'idle'}
                        className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#5D27A1] px-4 py-3 text-[13px] font-semibold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#471d7c] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#5D27A1]"
                        aria-label={status === 'idle' ? 'Subscribe' : status === 'verifying' ? 'Verifying' : 'Subscribing'}
                    >
                        <FontAwesomeIcon icon={faPaperPlane} className="h-3.5 w-3.5" />
                        <span>{status === 'idle' ? 'Subscribe' : status === 'verifying' ? 'Verifying...' : 'Subscribing...'}</span>
                    </button>
                </form>
                {error && <p className="text-[#ff6b6b] text-xs mt-2 px-1">{error}</p>}
                <p className="text-[11px] text-[#7a9f8a] leading-5">
                    ✓ No spam, unsubscribe anytime
                </p>
            </div>
        </div>
    )
}

export default FooterNewsletter

